import { isNone } from '@ember/utils';
import {
  validatePresence,
  validateNumber,
} from 'ember-changeset-validations/validators';
import { type ValidatorResult } from 'ember-changeset-validations';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { customMessageValidator } from 'uplisting-frontend/validators/custom-message';

const dayRateValidations = [
  validatePresence(true),
  validateNumber({ gte: 0, integer: true }),
];

function extraChargesDisabled(
  changes: Record<string, unknown>,
  content: Record<string, unknown>,
): boolean {
  const { extraCharges } = content as unknown as ModelRegistry['base-rate'];

  return !(changes['extraCharges'] ?? extraCharges);
}

const extraGuestValidations = [
  function (
    key: string,
    newValue: unknown,
    oldValue: unknown,
    changes: Record<string, unknown>,
    content: Record<string, unknown>,
  ): ValidatorResult {
    if (extraChargesDisabled(changes, content)) {
      return true;
    }

    return validateNumber({ gte: 0, integer: true })(
      key,
      newValue,
      oldValue,
      changes,
      content,
    );
  },
];

const maximumOccupancyValidation = [
  customMessageValidator(
    'maximum_occupancy_exceeds_capacity',
    function (
      key: string,
      newValue: unknown,
      oldValue: unknown,
      changes: Record<string, unknown>,
      content: Record<string, unknown>,
    ): ValidatorResult {
      if (extraChargesDisabled(changes, content)) {
        return true;
      }

      const baseRate = content as unknown as ModelRegistry['base-rate'];
      const propertyCapacity = baseRate.property?.maximumCapacity;

      const isValidNumber = validateNumber({ gte: 0, integer: true })(
        key,
        newValue,
        oldValue,
        changes,
        content,
      );

      if (isValidNumber !== true) {
        return isValidNumber;
      }

      if (isNone(propertyCapacity) || isNone(newValue)) {
        return true;
      }

      return (newValue as number) <= propertyCapacity;
    },
  ),
];

const extraChargeAfterValidation = [
  customMessageValidator(
    'extra_charge_after_too_high',
    function (
      key: string,
      newValue: unknown,
      oldValue: unknown,
      changes: Record<string, unknown>,
      content: Record<string, unknown>,
    ): ValidatorResult {
      if (extraChargesDisabled(changes, content)) {
        return true;
      }

      const baseRate = content as unknown as ModelRegistry['base-rate'];
      const maximumOccupancy =
        (changes['maximumOccupancy'] as number) ?? baseRate.maximumOccupancy;

      const isValidNumber = validateNumber({ gte: 0, integer: true })(
        key,
        newValue,
        oldValue,
        changes,
        content,
      );

      if (isValidNumber !== true) {
        return isValidNumber;
      }

      if (isNone(maximumOccupancy) || isNone(newValue)) {
        return true;
      }

      return (newValue as number) <= maximumOccupancy;
    },
  ),
];

export const BaseRateValidation = {
  sunday: dayRateValidations,
  monday: dayRateValidations,
  tuesday: dayRateValidations,
  wednesday: dayRateValidations,
  thursday: dayRateValidations,
  friday: dayRateValidations,
  saturday: dayRateValidations,

  extraGuestCharge: extraGuestValidations,
  extraChargeAfter: extraChargeAfterValidation,
  maximumOccupancy: maximumOccupancyValidation,
};
